/**
 * Названия действий журнала.
 *
 * Журнал и «История изменений» в карточках показывали машинный код действия:
 * `loyalty.tier_changed`, `pos.order_refunded`, `ops.device_revoked`. Журнал
 * читают, когда разбираются в споре — «кто это сделал и что именно», — и в
 * такой момент код на латинице требует переводчика.
 *
 * Незнакомое действие показывается кодом как есть. Это честнее прочерка: сразу
 * видно, что в словаре нет строки, и её надо добавить, а не что действия не
 * было.
 */
const ACTIONS: Readonly<Record<string, string>> = {
  'auth.locked': 'Учётная запись заблокирована после неудачных попыток',
  'auth.login': 'Вход в систему',
  'auth.login_failed': 'Неудачная попытка входа',
  'auth.login_throttled': 'Вход с адреса закрыт: слишком много неудачных попыток',
  'auth.logout': 'Выход из системы',
  'auth.password_changed': 'Смена пароля',
  'auth.totp_enrolled': 'Второй фактор подключён',
  'auth.totp_enrollment_started': 'Начато подключение второго фактора',
  'auth.totp_failed': 'Неверный код второго фактора',
  'auth.totp_locked': 'Второй фактор закрыт после неверных кодов',
  'auth.totp_verified': 'Второй фактор пройден',
  'bridge.balance_resolved': 'Решение по строке баланса при переносе',
  'bridge.catalog_imported': 'Каталог загружен из старой системы',
  'bridge.customers_committed': 'Клиенты перенесены из старой базы',
  'bridge.data_exported': 'Данные выгружены в открытом формате',
  'bridge.feedback_archived': 'Обращения перенесены в архив',
  'bridge.history_imported': 'История старой системы загружена',
  'bridge.migration_committed': 'Перенос данных применён',
  'bridge.migration_dry_run': 'Пробный прогон переноса',
  'bridge.migration_rolled_back': 'Перенос откачен',
  'bridge.migration_started': 'Перенос данных запущен',
  'bridge.migration_validated': 'Перенос проверен',
  'bridge.passes_prepared': 'Абонементы подготовлены к переносу',
  'cabinet.exported': 'Выгрузка из кабинета компании',
  'cabinet.manager_granted': 'Представителю открыт кабинет компании',
  'cabinet.manager_revoked': 'Представителю закрыт кабинет компании',
  'cabinet.signed_in': 'Клиент вошёл в кабинет',
  'cabinet.signed_out': 'Клиент вышел из кабинета',
  'comms.broadcast': 'Рассылка запущена',
  'comms.template_saved': 'Шаблон сохранён',
  'comms.trigger_saved': 'Сценарий сообщений сохранён',
  'consent.granted': 'Согласие выдано',
  'consent.revoked': 'Согласие отозвано',
  'consent.withdrawn': 'Согласие отозвано',
  'customer.company_attached': 'Водитель привязан к юрлицу',
  'customer.company_detached': 'Водитель снят с юрлица',
  'customer.created': 'Клиент заведён',
  'customer.forgotten': 'Клиент забыт по требованию',
  'customer.interaction_recorded': 'Записано обращение клиента из внешней системы',
  'customer.merged': 'Клиенты склеены',
  'customer.phone_verified': 'Телефон клиента подтверждён кодом',
  'customer.split': 'Склейка разделена',
  'customer.updated': 'Профиль клиента изменён',
  'dedup.pair_resolved': 'Пара дублей разобрана',
  'employee.created': 'Сотрудник заведён',
  'employee.password_set': 'Пароль сотруднику задан',
  'employee.pin_set': 'PIN сотруднику задан',
  'employee.role_granted': 'Роль выдана',
  'employee.role_revoked': 'Роль снята',
  'employee.sessions_revoked': 'Сеансы сотрудника прекращены',
  'employee.status_changed': 'Статус сотрудника изменён',
  'employee.totp_reset': 'Второй фактор сброшен',
  'employee.unlocked': 'Сотрудник разблокирован',
  'employee.updated': 'Сотрудник изменён',
  'feedback.answered': 'Ответ клиенту отправлен',
  'feedback.assigned': 'Назначен ответственный',
  'feedback.categorized': 'Категория подтверждена',
  'feedback.closed': 'Обращение закрыто',
  'feedback.escalated': 'Обращение эскалировано',
  'feedback.point_created': 'Добавлена точка сбора отзывов',
  'feedback.point_updated': 'Точка сбора отзывов изменена',
  'feedback.policy_reset': 'Особый срок ответа объекта снят',
  'feedback.policy_saved': 'Срок ответа на обращения изменён',
  'feedback.routing_rule_saved': 'Правило маршрутизации обращений сохранено',
  'identity.churn_model_trained': 'Модель оттока переобучена',
  'identity.conflict_resolved': 'Конфликт идентификации разрешён',
  'loyalty.accrual_reversed': 'Бонусы сняты при возврате',
  'loyalty.accrued': 'Бонусы начислены',
  'loyalty.adjusted': 'Ручная корректировка бонусов',
  'loyalty.entry_posted': 'Запись в реестр бонусов по основанию',
  'loyalty.expired': 'Бонусы сгорели по сроку',
  'loyalty.redeemed': 'Оплата бонусами',
  'loyalty.redemption_returned': 'Бонусы возвращены при возврате оплаты',
  'loyalty.referral_attached': 'Указан пригласивший клиент',
  'loyalty.referral_policy_changed': 'Изменены условия реферальной программы',
  'loyalty.referral_rewarded': 'Начислен бонус за приглашённого',
  'loyalty.reconciliation_drift': 'Расхождение баланса и реестра',
  'loyalty.rule_archived': 'Правило снято с публикации',
  'loyalty.rule_created': 'Правило создано',
  'loyalty.rule_published': 'Правило опубликовано',
  'loyalty.rule_restored': 'Версия правила восстановлена',
  'loyalty.rule_updated': 'Правило изменено',
  'loyalty.tier_changed': 'Уровень лояльности изменён',
  'ops.backup_completed': 'Резервная копия снята',
  'ops.equipment_secret_issued': 'Выдан секрет подписи контроллера оборудования',
  'ops.device_revoked': 'Доступ устройства отозван',
  'ops.release_finished': 'Выпуск завершён',
  'ops.release_rollback_started': 'Начат откат выпуска',
  'ops.release_rolled_back': 'Выпуск откачен',
  'ops.equipment_unit_created': 'Заведён узел оборудования',
  'ops.equipment_unit_updated': 'Изменён узел оборудования',
  'ops.release_started': 'Выпуск начат',
  'ops.residency_decided': 'Вердикт по резидентности',
  'ops.restore_drill': 'Учения по восстановлению',
  'ops.threshold_updated': 'Порог алерта изменён',
  'payments.card_binding_started': 'Клиент начал привязку карты',
  'payments.cards_erased': 'Привязанные карты обезличенного клиента стёрты',
  'payments.card_bound': 'Карта для автосписания привязана',
  'payments.card_removed': 'Карта для автосписания отвязана',
  'payments.charge_failed': 'Списание не прошло',
  'payments.charged': 'Списание проведено',
  'payments.fiscal_failed': 'Чек не передан в ОФД',
  'payments.fiscal_issued': 'Чек передан в ОФД',
  'payments.invoice_issued': 'Счёт выставлен',
  'payments.invoice_paid': 'Счёт оплачен',
  'payments.provider_recovered': 'Провайдер снова доступен',
  'payments.provider_unavailable': 'Провайдер недоступен',
  'payments.reconciliation_discrepancy': 'Расхождение при сверке',
  'payments.reconciliation_failed': 'Сверка с провайдером не прошла',
  'payments.refund_unknown': 'Банк не назвал итог возврата',
  'payments.statement_imported': 'Банковская выписка разнесена',
  'payments.refund_failed': 'Возврат не прошёл',
  'payments.refunded': 'Возврат проведён',
  'pos.cash_collected': 'Инкассация',
  'pos.device_enrolled': 'Касса поставлена на учёт',
  'pos.device_revoked': 'Доступ кассы отозван',
  'pos.material_created': 'Материал заведён',
  'pos.material_moved': 'Ручное движение материала на складе',
  'pos.material_norm_set': 'Норма расхода материала задана',
  'pos.material_updated': 'Материал изменён',
  'pos.materials_consumed': 'Материалы списаны',
  'pos.order_created': 'Заказ создан',
  'pos.order_paid': 'Заказ оплачен',
  'pos.order_refunded': 'Возврат по заказу',
  'pos.package_created': 'Пакет услуг заведён',
  'pos.package_price_changed': 'Цена пакета изменена',
  'pos.package_updated': 'Пакет услуг изменён',
  'pos.payment_failed': 'Оплата через провайдера не прошла',
  'pos.payment_resolved': 'Оплата к возврату разобрана',
  'pos.payment_started': 'Оплата через провайдера начата',
  'pos.pin_login': 'Вход кассира по PIN',
  'pos.pin_rejected': 'Неверный PIN на кассе',
  'pos.service_archived': 'Услуга в архиве',
  'pos.service_created': 'Услуга заведена',
  'pos.service_site_setting': 'Услуга включена или выключена на объекте',
  'pos.service_updated': 'Услуга изменена',
  'pos.shift_closed': 'Смена закрыта',
  'pos.shift_opened': 'Смена открыта',
  'pos.shift_reconciled': 'Смена сверена',
  'pos.tariff_changed': 'Тариф изменён',
  'pos.timesheet_recorded': 'Запись табеля',
  'privacy.retention_changed': 'Изменён срок хранения персональных данных',
  'report.exported': 'Отчёт выгружен',
  'report.policy_changed': 'Изменено определение метрик',
  'report.forecast_location_set': 'Заданы координаты объекта для прогноза',
  'segment.archived': 'Сегмент удалён в архив',
  'segment.created': 'Сегмент создан',
  'segment.members_exported': 'Выгружен состав сегмента',
  'segment.refreshed': 'Сегмент пересобран',
  'segment.updated': 'Сегмент изменён',
  'site.created': 'Объект заведён',
  'site.updated': 'Объект изменён',
  'subscription.cancelled': 'Подписка отменена',
  'subscription.charged': 'Списание по подписке',
  'subscription.converted': 'Подписка переведена на другой план',
  'subscription.created': 'Подписка оформлена',
  'subscription.paused': 'Подписка приостановлена',
  'subscription.plan.created': 'Тарифный план заведён',
  'subscription.plan.updated': 'Тарифный план изменён',
  'subscription.resumed': 'Подписка возобновлена',
  'subscription.retention_changed': 'Изменён сценарий удержания',
  'subscription.usage_recorded': 'Мойка по подписке учтена',
  'subscription.usage_reversed': 'Мойка по подписке возвращена в остаток',
  'subscription.vehicle_added': 'Автомобиль добавлен в семейный план',
  'subscription.vehicle_changed': 'Подписка перенесена на другой автомобиль',
  'subscription.vehicle_removed': 'Автомобиль снят с семейного плана',
  'tablet.enrolled': 'Планшет поставлен на учёт',
  'tablet.pin_changed': 'PIN на планшете изменён',
  'vehicle.linked': 'Автомобиль привязан к клиенту',
  'vehicle.link_confirmed': 'Владение машиной, заявленное в кабинете, подтверждено у стойки',
  'vehicle.unlinked': 'Связь клиента с автомобилем закрыта',
  'vehicle.updated': 'Атрибуты автомобиля изменены',
  'visit.completed': 'Визит закрыт',
  'visit.identification_declined': 'Клиент отказался от идентификации',
  'visit.identified': 'Клиент опознан на визите',
};

export function auditActionLabel(action: string): string {
  return ACTIONS[action] ?? action;
}

/** Есть ли перевод — нужно тесту, который стережёт полноту словаря. */
export function hasAuditActionLabel(action: string): boolean {
  return action in ACTIONS;
}
